import TruckIcon from "@redotech/redo-icons/truck-01.svg";
import type { DeliveryEstimateDateRange } from "@redotech/redo-model/conversion/conversion-autocheck";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useContext, useState } from "react";
import * as css from "./delivery-estimate.module.css";
import { ZipCodeContext } from "./zip-code-context";
import { ZipCodeModal } from "./zip-code-modal";

export const DeliveryEstimate = memo(function DeliveryEstimate({
  estimatedDeliveryDate,
  estimatedDeliveryDateRange,
}: {
  estimatedDeliveryDate: string;
  estimatedDeliveryDateRange?: DeliveryEstimateDateRange;
}) {
  const zipContext = useContext(ZipCodeContext);

  const [modalOpen, setModalOpen] = useState(false);

  const customerLocale =
    globalThis.Shopify?.locale ?? navigator.language ?? "en-US";
  const formattedEstimatedDeliveryDate = formatEstimatedDeliveryDate({
    customerLocale,
    estimatedDeliveryDate,
    estimatedDeliveryDateRange,
  });

  if (zipContext?.outsideUnitedStates) {
    return null;
  }

  return (
    <>
      <Flex align="center" gap="md" my="xl">
        <TruckIcon className={css.truckIcon} />
        <span className={css.text}>
          Get it by{" "}
          <span className={css.estimate}>{formattedEstimatedDeliveryDate}</span>
          {!!zipContext?.savedZipCode && (
            <span>
              {" "}
              to{" "}
              <span className={css.zipCode} onClick={() => setModalOpen(true)}>
                {zipContext.savedZipCode}
              </span>
            </span>
          )}
        </span>
      </Flex>
      <ZipCodeModal onClose={() => setModalOpen(false)} open={modalOpen} />
    </>
  );
});

function formatEstimatedDeliveryDate({
  customerLocale,
  estimatedDeliveryDate,
  estimatedDeliveryDateRange,
}: {
  customerLocale: string;
  estimatedDeliveryDate: string;
  estimatedDeliveryDateRange: DeliveryEstimateDateRange | undefined;
}) {
  if (!estimatedDeliveryDateRange) {
    return formatDate({ customerLocale, isoDate: estimatedDeliveryDate });
  }

  const formattedStartDate = formatDate({
    customerLocale,
    isoDate: estimatedDeliveryDateRange.start,
  });
  const formattedEndDate = formatDate({
    customerLocale,
    isoDate: estimatedDeliveryDateRange.end,
  });

  if (formattedStartDate === formattedEndDate) {
    return formattedStartDate;
  }

  return `${formattedStartDate} - ${formattedEndDate}`;
}

function formatDate({
  customerLocale,
  isoDate,
}: {
  customerLocale: string;
  isoDate: string;
}) {
  return new Date(isoDate).toLocaleDateString(customerLocale, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}
